import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import ProgressCard from '../../../../components/card/ProgressCard'
import PropTypes from 'prop-types'
import ParameterAction from '../actions/ParameterAction'
import ParameterGroupDto from '../dto/ParameterGroupDto'
import ParameterGroupLinkDto from '../dto/ParameterGroupLinkDto'
import { PATH_REFERENCIAL_PARAMETER } from 'home/constants/RouteConstants'
import useActions from 'utils/customHook/useActions'
import useTitle from 'utils/customHook/useTitle'
import { nbPerPageLabel, REFERENTIAL_JOBSYNC_ID } from 'referencial/constants/ReferencialConstants'
import { getUser } from 'utils/SettingUtils'
import { push } from '@lagunovsky/redux-react-router'
import { PATH_REFERENCIAL_PARAMETERS_GROUP_NEW } from '../../../../home/constants/RouteConstants'
import { CardTable } from 'components/datatable/NewTable'
import { Grid2 } from '@mui/material'
import JobSyncStatus from '../../JobSyncStatus'
import { reloadJobSync } from '../../../util/ReferencialUtils'
import ReferencialAction from '../../../action/ReferencialAction'
import { Stack } from '@mui/material'

const headers = ['id', 'name']

const ParametersGroupsApp = () => {
    const {
        parameterGroupLinks,
        parameterGroups,
        accountUser,
    } = useSelector(store => ({
        parameterGroupLinks: store.ParameterReducer.parameterGroupLinks,
        parameterGroups: store.ParameterReducer.parameterGroups,
        accountUser: store.AccountReducer.accountUser,
    }), shallowEqual)

    const [parameterGroupLoaded, setParameterGroupLoaded] = useState(false)
    const [parameterGroupLinksLoaded, setParameterGroupLinksLoaded] = useState(false)
    const [jobSync, setJobSync] = useState(undefined)

    useEffect(() => {
        reloadJobSync(REFERENTIAL_JOBSYNC_ID.parametergroup, setJobSync)
    }, [])

    useEffect(() => {
        if (jobSync && jobSync.executions[0]) {
            if (!jobSync.executions[0].statusCode) {
                setTimeout(() => {
                    reloadJobSync(REFERENTIAL_JOBSYNC_ID.parametergroup, setJobSync)
                }, 5000)
            }
        }
    }, [jobSync])

    const dispatch = useDispatch()

    useEffect(() => {
        dispatch(ParameterAction.fetchParameterGroups())
        dispatch(ParameterAction.fetchParameterGroupLinks())
    }, [])

    useEffect(() => {
        setParameterGroupLoaded(true)
    }, [parameterGroups])

    useEffect(() => {
        setParameterGroupLinksLoaded(true)
    }, [parameterGroupLinks])

    const isConsultant = useMemo(() => getUser().consultant === '1', [])

    useActions(() => {
        const isEnableEdit = accountUser.metadata === '1' || accountUser.isAdmin === '1'
        const baseActions = {
            referencialActions: {
                actions: {
                    sync: {
                        updateAction: options => dispatch(ReferencialAction.launchSyncJob(REFERENTIAL_JOBSYNC_ID.parametergroup, options, () => reloadJobSync(REFERENTIAL_JOBSYNC_ID.parametergroup, setJobSync))),
                    },
                },
            },
        }
        const newAction = (isEnableEdit && !isConsultant) ? {
            new: () => dispatch(push(PATH_REFERENCIAL_PARAMETERS_GROUP_NEW)),
        } : {}
        return { ...baseActions, ...newAction }
    }, [])

    useTitle(() => [{
        title: i18n.referencials,
        href: 'referencial',
    }, {
        title: i18n.parameterGroup,
        href: PATH_REFERENCIAL_PARAMETER,
    }], [])

    const formatData = (data) => data.map(p => ({ ...p, id: p.code }))

    const classGroupsCodes = useMemo(() => parameterGroupLinks.filter(p => p.classGroupCode).map(p => p.classGroupCode), [parameterGroupLinks])
    const usageGroupsCodes = useMemo(() => [
        ...parameterGroupLinks.filter(p => p.usageGroupCode).map(p => p.usageGroupCode ? p.usageGroupCode : ''),
        ...parameterGroupLinks.filter(p => p.usageGroupCode2).map(p => p.usageGroupCode2 ? p.usageGroupCode2 : ''),
        ...parameterGroupLinks.filter(p => p.usageGroupCode3).map(p => p.usageGroupCode3 ? p.usageGroupCode3 : ''),
        ...parameterGroupLinks.filter(p => p.usageGroupCode4).map(p => p.usageGroupCode4 ? p.usageGroupCode4 : ''),
        ...parameterGroupLinks.filter(p => p.usageGroupCode5).map(p => p.usageGroupCode5 ? p.usageGroupCode5 : ''),
    ], [parameterGroupLinks])

    const classData = useMemo(() => formatData(parameterGroups.filter(p => classGroupsCodes.includes(p.code) || !usageGroupsCodes.includes(p.code))), [classGroupsCodes, parameterGroups, usageGroupsCodes])
    const usageData = useMemo(() => formatData(parameterGroups.filter(p => usageGroupsCodes.includes(p.code))), [parameterGroups, usageGroupsCodes])

    if (!parameterGroupLoaded || !parameterGroupLinksLoaded) {
        return <ProgressCard indeterminate />
    }

    return (
        <Stack margin={1} spacing={1}>
            <Grid2 container justifyContent='flex-end' className='padding-left-1'>
                <JobSyncStatus jobSync={jobSync}/>
            </Grid2>
            <CardTable
                color='bannerColor'
                title={i18n.classGroup}
                rows={classData}
                headers={headers}
                rowsPerPageOptions={nbPerPageLabel}
                onClickRow={(element) => {
                    dispatch(push(`/referencial/parameterGroup/${element.id}/dashboard`))
                }}
            />
            <CardTable
                color='bannerColor'
                title={i18n.usageGroup}
                rows={usageData}
                headers={headers}
                rowsPerPageOptions={nbPerPageLabel}
                onClickRow={(element) => {
                    dispatch(push(`/referencial/parameterGroup/${element.id}/dashboard`))
                }}
            />
        </Stack>
    )
}

ParametersGroupsApp.propTypes = {
    parameterGroupLinks: PropTypes.arrayOf(PropTypes.instanceOf(ParameterGroupLinkDto)),
    parameterGroups: PropTypes.arrayOf(PropTypes.instanceOf(ParameterGroupDto)),
    forceFetch: PropTypes.func,
    fetchParameterGroupLinks: PropTypes.func,
    fetchParameterGroups: PropTypes.func,
}

export default ParametersGroupsApp